import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  claimGame,
  fetchGameEvents,
  pushEvents,
  releaseClaim,
  setScoringState,
  watchGame,
  type ClaimOutcome,
} from '../data/live'
import { fromServerEvent } from '../data/live'
import { useAuth } from '../auth/AuthProvider'
import { outbox } from '../domain/merge'
import type { Game, ID, Player, StatEvent } from '../domain/types'

/**
 * The scoring device's half of step 2: hold the pen, publish the grid, and get
 * the taps up to the server.
 *
 * The ordering rule that shapes everything here: **the local scoresheet wins.**
 * A tap is written to IndexedDB and drawn on screen before this hook knows it
 * exists, and nothing in here can refuse, delay or undo one. The iPad has to be
 * able to score a whole game in a stadium with no signal -- that is the original
 * premise of the app -- so the network is a courier, never a gatekeeper.
 *
 * The one thing that DOES stop scoring is being positively told somebody else
 * has taken over, because from that moment this device's copy is a fork rather
 * than the record. Not being able to ask is different from being told no, and is
 * treated differently: it keeps scoring.
 */

export type PenState =
  | 'not-syncable' // a manual game with no PlayHQ fixture: nothing to sync to
  | 'checking'
  | 'mine'
  | 'theirs'
  | 'offline' // could not reach the server; keep scoring, keep queueing
  | 'refused' // the server answered, and said no

export interface GameSync {
  pen: PenState
  holderName: string | null
  claimedAt: string | null
  isStale: boolean
  /** Events written here but not yet acknowledged by the server. */
  pending: number
  lastPushedAt: number | null
  /** The last thing that went wrong, in a sentence a human can act on. */
  error: string | null
  connected: boolean
  takeOver(): Promise<void>
  release(reason?: 'ended' | 'manual'): Promise<void>
  /** Pull everything the server has for this game, e.g. after taking over. */
  pullAll(): Promise<StatEvent[]>
}

const PUSH_DEBOUNCE_MS = 1500
const RECHECK_MS = 60_000

export function useGameSync(args: {
  game: Game | null
  events: StatEvent[]
  players: Player[]
  onSynced(ids: ID[], at: number): void
  currentPeriod: number
  periodFormat: string
  enabledStatTypes: string[]
}): GameSync {
  const { game, events, players, onSynced, currentPeriod, periodFormat, enabledStatTypes } = args
  const { session, member } = useAuth()

  const syncable = Boolean(game?.playhqGameId) && Boolean(session)
  const canScore = member?.role === 'scorer' || member?.role === 'admin'

  const [pen, setPen] = useState<PenState>(syncable ? 'checking' : 'not-syncable')
  const [holderName, setHolderName] = useState<string | null>(null)
  const [claimedAt, setClaimedAt] = useState<string | null>(null)
  const [isStale, setIsStale] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastPushedAt, setLastPushedAt] = useState<number | null>(null)
  const [connected, setConnected] = useState(false)

  const gameId = game?.id ?? null
  const pushing = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Anything the server has not acknowledged at its current version.
  const pendingEvents = useMemo(() => outbox(events), [events])
  const outboxRef = useRef(pendingEvents)
  outboxRef.current = pendingEvents
  const playersRef = useRef(players)
  playersRef.current = players

  const applyOutcome = useCallback((o: ClaimOutcome) => {
    setHolderName(o.holder_name ?? null)
    setClaimedAt(o.claimed_at ?? null)
    setIsStale(o.is_stale ?? false)
    setPen(o.mine ? 'mine' : 'theirs')
  }, [])

  const check = useCallback(
    async (takeover = false) => {
      if (!gameId || !syncable) return
      if (!canScore) {
        setPen('theirs')
        return
      }
      const res = await claimGame(gameId, takeover, deviceLabel())
      if (!res.ok) {
        // Told nothing, so assume nothing. Scoring continues; the outbox grows.
        setPen((prev) => (prev === 'mine' ? 'mine' : 'offline'))
        setError(res.error)
        return
      }
      setError(null)
      applyOutcome(res.data)
    },
    [gameId, syncable, canScore, applyOutcome],
  )

  // Claim on open, and re-check periodically: a claim can be taken while this
  // device is asleep in someone's bag, and realtime does not survive that.
  useEffect(() => {
    if (!syncable || !gameId) {
      setPen('not-syncable')
      return
    }
    setPen('checking')
    void check()
    const id = setInterval(() => void check(), RECHECK_MS)
    return () => clearInterval(id)
  }, [syncable, gameId, check])

  // Realtime: the moment somebody else takes the pen, stop pretending.
  useEffect(() => {
    if (!syncable || !gameId) return
    return watchGame(gameId, {
      onClaim: () => void check(),
      onStatus: (s) => setConnected(s === 'connected'),
    })
  }, [syncable, gameId, check])

  // Publish the grid so watchers draw the same one, including players who have
  // not touched the ball yet.
  useEffect(() => {
    if (pen !== 'mine' || !game) return
    const roster = game.roster.map((id) => {
      const p = players.find((x) => x.id === id)
      return { id, name: p?.name ?? 'Unknown', isFillIn: p?.isFillIn === true }
    })
    void setScoringState(game.id, {
      roster,
      current_period: currentPeriod,
      period_format: periodFormat,
      enabled_stat_types: enabledStatTypes,
      home_away: game.homeAway,
      opponent_name: game.opponentName ?? null,
    })
    // players is intentionally omitted: a rename mid-game is not worth a write,
    // and the roster ids are what the watchers actually key on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pen, game?.id, game?.roster.join(','), currentPeriod, periodFormat, enabledStatTypes.join(',')])

  const flush = useCallback(async () => {
    if (pushing.current || !gameId) return
    const batch = outboxRef.current.slice(0, 500)
    if (batch.length === 0) return
    pushing.current = true
    try {
      const res = await pushEvents(batch, playersRef.current)
      if (!res.ok) {
        setError(res.error)
        setPen((prev) => (prev === 'mine' ? 'mine' : prev))
        return
      }
      if (res.data.rejected.length > 0) {
        // The pen moved while we were tapping. Say so loudly: these taps exist
        // here and nowhere else, and silently dropping them is exactly the
        // failure this project keeps being bitten by.
        setError(res.data.rejected[0]!.reason)
        await check()
        return
      }
      const at = Date.now()
      setError(null)
      setLastPushedAt(at)
      onSynced(
        batch.map((e) => e.id),
        at,
      )
    } finally {
      pushing.current = false
    }
  }, [gameId, onSynced, check])

  // Debounced: a run of taps is one round trip, not ten.
  useEffect(() => {
    if (pen !== 'mine' || pendingEvents.length === 0) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => void flush(), PUSH_DEBOUNCE_MS)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [pen, pendingEvents.length, flush])

  // Coming back online is the moment the queue matters most.
  useEffect(() => {
    const onOnline = () => {
      void check().then(() => flush())
    }
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [check, flush])

  const takeOver = useCallback(async () => {
    await check(true)
  }, [check])

  const release = useCallback(
    async (reason: 'ended' | 'manual' = 'manual') => {
      if (!gameId || !syncable) return
      // One last flush first: releasing with taps still queued would strand them.
      await flush()
      await releaseClaim(gameId, reason)
    },
    [gameId, syncable, flush],
  )

  const pullAll = useCallback(async () => {
    if (!gameId) return []
    const res = await fetchGameEvents(gameId)
    if (!res.ok) {
      setError(res.error)
      return []
    }
    return res.data.map(fromServerEvent)
  }, [gameId])

  return {
    pen,
    holderName,
    claimedAt,
    isStale,
    pending: pendingEvents.length,
    lastPushedAt,
    error,
    connected,
    takeOver,
    release,
    pullAll,
  }
}

/** Enough to tell two devices apart in the claim history, and nothing more. */
function deviceLabel(): string {
  const ua = navigator.userAgent
  if (/iPad/.test(ua)) return 'iPad'
  if (/iPhone/.test(ua)) return 'iPhone'
  if (/Android/.test(ua)) return 'Android phone'
  return 'Browser'
}
